import React, { useEffect, useState } from "react";
import api, { API, formatRupiah, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, Save, Package } from "lucide-react";

function emptyLine() { return { product_id: "", nama: "", deskripsi: "", harga: 0, qty: 1 }; }

export default function SuratDetailDialog({ open, onOpenChange, surat, onSaved }) {
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([emptyLine()]);
  const [diskon, setDiskon] = useState(0);
  const [usePpn, setUsePpn] = useState(false);
  const [ppn, setPpn] = useState(11);
  const [usePph, setUsePph] = useState(false);
  const [pph, setPph] = useState(2);
  const [useStages, setUseStages] = useState(false);
  const [stages, setStages] = useState([{ label: "DP 50%", amount: 0 }]);
  const [extra, setExtra] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/products").then(r => setProducts(r.data)).catch(()=>{});
    const d = surat?.detail;
    if (d) {
      setLines(d.products?.length ? d.products.map(p => ({...p, qty: p.qty||1})) : [emptyLine()]);
      setDiskon(d.diskon || 0);
      setUsePpn(!!d.ppn);
      setPpn(d.ppn || 11);
      setUsePph(!!d.pph);
      setPph(d.pph || 2);
      setUseStages(!!d.payment_stages?.length);
      setStages(d.payment_stages?.length ? d.payment_stages : [{ label: "DP 50%", amount: 0 }]);
      setExtra((d.syarat_extra || []).join("\n"));
    } else {
      setLines([emptyLine()]);
      setDiskon(0);
      setUsePpn(false);
      setUsePph(false);
      setUseStages(false);
      setStages([{label:"DP 50%", amount:0}]);
      setExtra("");
    }
  }, [open, surat]);

  const subtotal = lines.reduce((a,l)=>a + (Number(l.harga)||0)*(Number(l.qty)||0), 0);
  const base = subtotal - (Number(diskon)||0);
  const ppnAmt = usePpn ? base * (Number(ppn)||0)/100 : 0;
  const pphAmt = usePph ? base * (Number(pph)||0)/100 : 0;
  const total = base + ppnAmt - pphAmt;

  function pickProduct(idx, pid) {
    const p = products.find(x => x.id === pid);
    if (!p) return;
    const arr = [...lines];
    arr[idx] = { product_id: pid, nama: p.nama, deskripsi: p.deskripsi || "", harga: p.harga || 0, qty: arr[idx].qty || 1 };
    setLines(arr);
  }

  async function saveAndDownload(download) {
    const body = {
      products: lines.filter(l => l.nama).map(l => ({
        nama: l.nama, deskripsi: l.deskripsi || null,
        harga: Number(l.harga)||0, qty: Number(l.qty)||0,
      })),
      diskon: Number(diskon)||0,
      ppn: usePpn ? Number(ppn)||0 : 0,
      pph: usePph ? Number(pph)||0 : 0,
      payment_stages: useStages ? stages.map(s => ({label: s.label, amount: Number(s.amount)||0})).filter(s=>s.label) : [],
      syarat_extra: extra.split("\n").map(s=>s.replace(/\s+$/, "")),
    };
    if (!body.products.length) return toast.error("Tambahkan minimal 1 produk");
    setSaving(true);
    try {
      await api.put(`/surat/${surat.id}/detail`, body);
      toast.success("Detail tersimpan");
      if (onSaved) onSaved();
      if (download) {
        // open in new tab to preserve cookie
        window.open(`${API}/surat/${surat.id}/pdf`, "_blank");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="surat-detail-dialog">
        <DialogHeader><DialogTitle>Detail & PDF — {surat?.nomor}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          {/* Lines */}
          <div className="rounded-md border border-border">
            <div className="grid grid-cols-12 gap-2 bg-secondary/40 px-3 py-2 text-xs font-medium text-ink-500">
              <div className="col-span-5">Produk</div><div className="col-span-3 text-right">Harga</div><div className="col-span-1 text-right">Qty</div><div className="col-span-2 text-right">Jumlah</div><div className="col-span-1" />
            </div>
            <div className="divide-y divide-border">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <div className="col-span-5">
                    <Select value={l.product_id||"_custom"} onValueChange={(v) => v==="_custom" ? setLines(arr => arr.map((x,j)=>j===i?{...x, product_id:""}:x)) : pickProduct(i, v)}>
                      <SelectTrigger className="h-8"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                      <SelectContent><SelectItem value="_custom">— Custom —</SelectItem>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={l.nama} placeholder="Nama produk" className="mt-1 h-8 text-sm" onChange={e=>setLines(arr=>arr.map((x,j)=>j===i?{...x, nama:e.target.value}:x))} />
                  </div>
                  <div className="col-span-3"><Input type="number" value={l.harga} className="h-8 text-right font-mono text-xs" onChange={e=>setLines(arr=>arr.map((x,j)=>j===i?{...x,harga:e.target.value}:x))} /></div>
                  <div className="col-span-1"><Input type="number" value={l.qty} className="h-8 text-right" onChange={e=>setLines(arr=>arr.map((x,j)=>j===i?{...x,qty:e.target.value}:x))} /></div>
                  <div className="col-span-2 text-right font-mono text-sm">{formatRupiah((Number(l.harga)||0)*(Number(l.qty)||0))}</div>
                  <div className="col-span-1 text-right"><Button variant="ghost" size="icon" onClick={()=>setLines(arr=>arr.filter((_,j)=>j!==i))}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
                </div>
              ))}
            </div>
            <div className="px-3 py-2 border-t border-border"><Button variant="outline" size="sm" onClick={()=>setLines([...lines, emptyLine()])} data-testid="add-line"><Plus className="mr-1 h-3 w-3" /> Tambah baris</Button></div>
          </div>

          {/* Adjustments */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-md border border-border p-3">
              <Label>Diskon (Rp)</Label>
              <Input type="number" value={diskon} onChange={e=>setDiskon(e.target.value)} className="font-mono" data-testid="inp-diskon" />
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between"><Label>PPN</Label><Switch checked={usePpn} onCheckedChange={setUsePpn} data-testid="switch-ppn" /></div>
              {usePpn && <Input type="number" value={ppn} onChange={e=>setPpn(e.target.value)} className="mt-2 font-mono" placeholder="11" />}
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between"><Label>PPh</Label><Switch checked={usePph} onCheckedChange={setUsePph} data-testid="switch-pph" /></div>
              {usePph && <Input type="number" value={pph} onChange={e=>setPph(e.target.value)} className="mt-2 font-mono" placeholder="2" />}
            </div>
          </div>

          {/* Stages */}
          <div className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between"><Label>Termin Pembayaran (Bertahap)</Label><Switch checked={useStages} onCheckedChange={setUseStages} data-testid="switch-stages" /></div>
            {useStages && (
              <div className="mt-2 space-y-2">
                {stages.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-7"><Input value={s.label} placeholder={`Tahap ${i+1}`} onChange={e=>setStages(arr=>arr.map((x,j)=>j===i?{...x,label:e.target.value}:x))} /></div>
                    <div className="col-span-4"><Input type="number" value={s.amount} className="text-right font-mono" onChange={e=>setStages(arr=>arr.map((x,j)=>j===i?{...x,amount:e.target.value}:x))} /></div>
                    <div className="col-span-1 text-right"><Button variant="ghost" size="icon" onClick={()=>setStages(arr=>arr.filter((_,j)=>j!==i))}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={()=>setStages([...stages, {label:`Tahap ${stages.length+1}`, amount:0}])}><Plus className="mr-1 h-3 w-3" /> Tambah Tahap</Button>
              </div>
            )}
          </div>

          <div>
            <Label>Syarat & Ketentuan Tambahan (1 baris = 1 poin)</Label>
            <Textarea rows={6} value={extra} onChange={e=>setExtra(e.target.value)} placeholder={"Misal:\n## SYARAT PEMBAYARAN\nPembayaran **50%** di muka sebagai DP.\nPelunasan paling lambat *7 hari* setelah serah terima.\n\n## SYARAT LAIN\nGaransi __1 tahun__ untuk semua produk."} data-testid="input-syarat-extra" />
            <div className="mt-1 rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2 text-[11px] leading-relaxed text-ink-500">
              <span className="font-medium text-ink-700">Format yang didukung:</span>{" "}
              <code className="rounded bg-ink-900/5 px-1 py-0.5 font-mono text-[10px]">**tebal**</code> → <b>tebal</b>{" · "}
              <code className="rounded bg-ink-900/5 px-1 py-0.5 font-mono text-[10px]">*miring*</code> → <i>miring</i>{" · "}
              <code className="rounded bg-ink-900/5 px-1 py-0.5 font-mono text-[10px]">__garis bawah__</code> → <u>garis bawah</u>{" · "}
              <code className="rounded bg-ink-900/5 px-1 py-0.5 font-mono text-[10px]">## Judul</code> → <b>SUB-JUDUL</b>{" · "}
              Baris kosong = jeda spasi.
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-md border border-border bg-secondary/30 p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{formatRupiah(subtotal)}</span></div>
            <div className="flex justify-between text-rose-700"><span>Diskon</span><span className="font-mono">− {formatRupiah(Number(diskon)||0)}</span></div>
            {usePpn && <div className="flex justify-between text-emerald-700"><span>PPN ({ppn}%)</span><span className="font-mono">+ {formatRupiah(ppnAmt)}</span></div>}
            {usePph && <div className="flex justify-between text-rose-700"><span>PPh ({pph}%)</span><span className="font-mono">− {formatRupiah(pphAmt)}</span></div>}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-border"><span>TOTAL</span><span className="font-mono text-ink-900">{formatRupiah(total)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Batal</Button>
          <Button onClick={()=>saveAndDownload(false)} disabled={saving} variant="outline" data-testid="save-detail"><Save className="mr-1 h-4 w-4" /> Simpan</Button>
          <Button onClick={()=>saveAndDownload(true)} disabled={saving} className="bg-brand text-white hover:bg-brand-600" data-testid="save-and-download"><FileDown className="mr-1 h-4 w-4" /> Simpan & Unduh PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
